<div class="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#09090b] dark:text-slate-100 flex flex-col antialiased selection:bg-emerald-600 selection:text-white transition-colors duration-200">
  <!-- Application Navigation Header -->
  <header class="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur px-4 py-3 flex items-center justify-between shadow-xs sticky top-0 z-40">
    <div class="flex items-center space-x-2">
      <span class="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse"></span>
      <span class="text-xs font-bold tracking-wider uppercase text-slate-700 dark:text-slate-300">
        League of Managers
      </span>
    </div>

    <div class="relative">
      <!-- Settings Wheel / User Menu Button -->
      <button
        type="button"
        (click)="toggleUserMenu()"
        id="btn-private-settings"
        title="Ajustes de usuario"
        class="flex items-center space-x-2 px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/80 dark:bg-slate-900/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer shadow-xs"
      >
        <div class="w-6 h-6 rounded-lg bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
          {{ currentUser()?.name?.charAt(0) || 'M' }}
        </div>
        <span class="hidden sm:inline text-xs font-semibold text-slate-800 dark:text-slate-200">
          {{ currentUser()?.name || 'Mánager' }}
        </span>
        <app-icon name="settings" [size]="15" class="text-slate-500 dark:text-slate-400" />
      </button>

      @if (isUserMenuOpen()) {
        <!-- Backdrop click overlay -->
        <div (click)="closeUserMenu()" class="fixed inset-0 z-40"></div>

        <div id="private-user-menu-dropdown" class="absolute right-0 mt-2 w-72 sm:w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl py-2 z-50 text-xs transition-colors">
          <!-- Info de usuario -->
          @if (currentUser(); as user) {
            <div class="px-3.5 py-2.5 border-b border-slate-100 dark:border-slate-800">
              <div class="flex items-center space-x-2.5">
                <div class="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                  {{ user.name.charAt(0) || 'M' }}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="font-bold text-slate-900 dark:text-white truncate">{{ user.name }}</p>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">{{ user.email }}</p>
                </div>
              </div>
              <div class="mt-2 flex items-center justify-between">
                <span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800/60">
                  {{ user.role }}
                </span>
                @if (context()?.club?.name; as clubName) {
                  <span class="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[150px] font-medium">
                    {{ clubName }}
                  </span>
                }
              </div>
            </div>
          }

          <div class="p-1 space-y-0.5">
            <!-- Modo Oscuro / Claro -->
            <button
              type="button"
              (click)="toggleDarkMode()"
              id="private-menu-theme"
              class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
            >
              <div class="flex items-center space-x-2.5">
                @if (isDarkMode()) {
                  <app-icon name="sun" [size]="16" class="text-amber-400" />
                  <span class="font-medium">Modo Claro</span>
                } @else {
                  <app-icon name="moon" [size]="16" class="text-slate-600 dark:text-slate-300" />
                  <span class="font-medium">Modo Oscuro</span>
                }
              </div>
              <div class="w-8 h-4.5 rounded-full p-0.5 transition-colors" [class.bg-emerald-600]="isDarkMode()" [class.bg-slate-300]="!isDarkMode()" [class.dark:bg-slate-700]="!isDarkMode()">
                <div class="w-3.5 h-3.5 rounded-full bg-white transition-transform" [class.translate-x-3.5]="isDarkMode()" [class.translate-x-0]="!isDarkMode()"></div>
              </div>
            </button>

            <!-- Cerrar Sesión -->
            @if (currentUser()) {
              <div class="pt-1 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  (click)="handleLogout()"
                  id="private-menu-logout"
                  class="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer transition-colors font-semibold"
                >
                  <app-icon name="log-out" [size]="15" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </header>

  <!-- Main View Content -->
  <main class="flex-1 max-w-5xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
    <!-- Top Bar: Navigation & Action -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2 border-b border-slate-200 dark:border-slate-800">
      <a
        routerLink="/leagues"
        class="inline-flex items-center space-x-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors group cursor-pointer"
      >
        <app-icon name="arrow-left" [size]="16" class="group-hover:-translate-x-0.5 transition-transform" />
        <span>Mis competiciones</span>
      </a>

      <div class="flex items-center space-x-2">
        <button
          type="button"
          (click)="reload()"
          [disabled]="loading() || busy()"
          class="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-semibold shadow-xs transition-colors cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
        >
          <app-icon name="refresh-cw" [size]="14" [class.animate-spin]="loading() || busy()" />
          <span>Actualizar estado</span>
        </button>
      </div>
    </div>

    <!-- Loading Feedback -->
    @if (loading()) {
      <div role="status" class="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 flex items-center justify-center space-x-3 text-slate-600 dark:text-slate-300 shadow-xs">
        <app-icon name="refresh-cw" [size]="20" class="animate-spin text-emerald-600" />
        <p class="text-sm font-medium">Consultando tu club privado…</p>
      </div>
    }

    <!-- Error Alert Banner -->
    @if (error()) {
      <div role="alert" class="p-4 sm:p-5 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-800 dark:text-rose-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-xs">
        <div class="flex items-start space-x-3">
          <app-icon name="alert-circle" [size]="20" class="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <p class="text-sm font-medium leading-relaxed">{{ error() }}</p>
        </div>
        <button
          type="button"
          (click)="reload()"
          class="inline-flex items-center justify-center space-x-1.5 px-3.5 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs transition-colors shrink-0 cursor-pointer shadow-xs"
        >
          <app-icon name="refresh-cw" [size]="13" />
          <span>Reintentar consulta</span>
        </button>
      </div>
    }

    <!-- Capability Status / Info Banner -->
    @if (capabilityError()) {
      <div role="status" class="p-4 sm:p-5 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-amber-800 dark:text-amber-200 flex items-start space-x-3 shadow-xs">
        <app-icon name="alert-triangle" [size]="18" class="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p class="text-sm font-medium leading-relaxed">{{ capabilityError() }}</p>
      </div>
    }

    @if (context(); as league) {
      <div class="text-sm text-slate-700 dark:text-slate-300">
        @if(cancelled(league.leagueState)) { <p role="status">{{league.leagueState === 'CANCELLED' ? 'Liga cancelada. Sus datos se conservan como historial.' : 'Cancelando la liga. Esperando confirmación de todos los servicios.'}}</p> }
      </div>
      <!-- Hero Header Card: League & Club Information -->
      <div class="p-6 sm:p-8 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/90 shadow-sm relative overflow-hidden">
        <div class="absolute -top-12 -right-12 w-48 h-48 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div class="space-y-1.5 min-w-0">
            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                <app-icon name="shield" [size]="12" class="text-emerald-600 dark:text-emerald-400" />
                <span>Liga Privada</span>
              </span>
              <span
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border"
                [class.bg-emerald-50]="league.provisioning.status === 'READY'"
                [class.text-emerald-700]="league.provisioning.status === 'READY'"
                [class.border-emerald-200]="league.provisioning.status === 'READY'"
                [class.dark:bg-emerald-950/40]="league.provisioning.status === 'READY'"
                [class.dark:text-emerald-400]="league.provisioning.status === 'READY'"
                [class.dark:border-emerald-800]="league.provisioning.status === 'READY'"
                [class.bg-amber-50]="league.provisioning.status === 'PENDING'"
                [class.text-amber-700]="league.provisioning.status === 'PENDING'"
                [class.border-amber-200]="league.provisioning.status === 'PENDING'"
                [class.dark:bg-amber-950/40]="league.provisioning.status === 'PENDING'"
                [class.dark:text-amber-400]="league.provisioning.status === 'PENDING'"
                [class.dark:border-amber-800]="league.provisioning.status === 'PENDING'"
                [class.bg-rose-50]="league.provisioning.status === 'FAILED'"
                [class.text-rose-700]="league.provisioning.status === 'FAILED'"
                [class.border-rose-200]="league.provisioning.status === 'FAILED'"
                [class.dark:bg-rose-950/40]="league.provisioning.status === 'FAILED'"
                [class.dark:text-rose-400]="league.provisioning.status === 'FAILED'"
                [class.dark:border-rose-800]="league.provisioning.status === 'FAILED'"
              >
                {{ league.provisioning.status }}
              </span>
            </div>

            <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              {{ league.name }}
            </h1>

            <h2 class="text-lg font-semibold text-slate-700 dark:text-slate-300 flex items-center space-x-2">
              <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              <span>{{ league.club.name }}</span>
            </h2>

            <div class="pt-1">
              <p class="text-xs text-slate-500 dark:text-slate-400 font-mono">
                Estado: {{ league.leagueState }} · Preparación: {{ league.provisioning.status }}
              </p>
            </div>
          </div>

          <!-- Main Enter Club Action Button -->
          <div class="sm:self-center shrink-0 flex flex-col items-stretch sm:items-end gap-1.5">
            <button
              type="button"
              id="btn-open-private-club"
              [disabled]="!canOpenClub()"
              (click)="enter()"
              [title]="!isLeagueActive() ? 'La liga debe estar activa para poder abrir el club' : 'Abrir club'"
              class="w-full sm:w-auto inline-flex items-center justify-center space-x-2.5 px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all cursor-pointer"
            >
              <span>Abrir club</span>
              <app-icon name="arrow-right" [size]="16" />
            </button>
            @if (league.leagueState !== 'ACTIVE' && !cancelled(league.leagueState)) {
              <span class="text-[11px] font-medium text-amber-600 dark:text-amber-400 text-center sm:text-right">
                La liga debe estar activa para abrir el club
              </span>
            }
          </div>
        </div>

        @if (!cancelled(league.leagueState) && league.provisioning.status === 'FAILED') {
          <div class="mt-4 p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/60 text-rose-800 dark:text-rose-200 text-xs flex items-start space-x-2.5">
            <app-icon name="alert-triangle" [size]="16" class="shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
            <p>La preparación necesita recuperación administrativa. Tu pertenencia y club se conservan; no vuelvas a crear otro.</p>
          </div>
        }
      </div>

      <!-- Preparation Checklist & Rules Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Club Provisioning Progress Card -->
        <div class="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/80 shadow-xs flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <span class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Alistamiento
              </span>
              <app-icon name="check-circle" [size]="16" class="text-emerald-500" />
            </div>

            <ul class="mt-3.5 space-y-2 text-xs font-medium text-slate-700 dark:text-slate-300">
              <li class="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <span class="flex items-center space-x-2">
                  <span class="w-1.5 h-1.5 rounded-full" [class.bg-emerald-500]="league.provisioning.clubReady" [class.bg-amber-400]="!league.provisioning.clubReady"></span>
                  <span>Club: {{ league.provisioning.clubReady ? 'Preparado' : 'Pendiente' }}</span>
                </span>
                <span class="text-[11px] font-bold" [class.text-emerald-600]="league.provisioning.clubReady" [class.text-amber-500]="!league.provisioning.clubReady">
                  {{ league.provisioning.clubReady ? 'LISTO' : 'EN COLA' }}
                </span>
              </li>
              <li class="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <span class="flex items-center space-x-2">
                  <span class="w-1.5 h-1.5 rounded-full" [class.bg-emerald-500]="league.provisioning.squadReady" [class.bg-amber-400]="!league.provisioning.squadReady"></span>
                  <span>Plantilla: {{ league.provisioning.squadReady ? 'Preparada' : 'Pendiente' }}</span>
                </span>
                <span class="text-[11px] font-bold" [class.text-emerald-600]="league.provisioning.squadReady" [class.text-amber-500]="!league.provisioning.squadReady">
                  {{ league.provisioning.squadReady ? 'LISTA' : 'EN COLA' }}
                </span>
              </li>
              <li class="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <span class="flex items-center space-x-2">
                  <span class="w-1.5 h-1.5 rounded-full" [class.bg-emerald-500]="league.provisioning.economyReady" [class.bg-amber-400]="!league.provisioning.economyReady"></span>
                  <span>Economía: {{ league.provisioning.economyReady ? 'Preparada' : 'Pendiente' }}</span>
                </span>
                <span class="text-[11px] font-bold" [class.text-emerald-600]="league.provisioning.economyReady" [class.text-amber-500]="!league.provisioning.economyReady">
                  {{ league.provisioning.economyReady ? 'LISTA' : 'EN COLA' }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Effective Round Information -->
        <div class="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/80 shadow-xs flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <span class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Jornada de Entrada
              </span>
              <app-icon name="calendar" [size]="16" class="text-sky-500" />
            </div>

            <div class="mt-3.5 space-y-2">
              @if (league.effectiveJoiningRound; as round) {
                <div class="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/40 border border-sky-200/80 dark:border-sky-800/60">
                  <p class="text-xs font-medium text-sky-900 dark:text-sky-200 leading-relaxed">
                    Primera jornada efectiva: {{ round }}. Las jornadas anteriores desde la activación pueden contar como derrotas administrativas, sin partidos ni goles inventados.
                  </p>
                </div>
              } @else {
                <div class="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/60">
                  <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    La primera jornada efectiva todavía no está asignada.
                  </p>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Financial & Match Rules Card -->
        <div class="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/80 shadow-xs flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <span class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Reglas Económicas
              </span>
              <app-icon name="info" [size]="16" class="text-emerald-500" />
            </div>

            <div class="mt-3.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/60">
              <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Los descansos no generan partido ni taquilla. Los salarios e intereses siguen las obligaciones reales de la jornada.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- League Details Section (Members & Creator Management) -->
      @if (details(); as view) {
          @if(cancelled(league.leagueState)) { <p>Servicios confirmados: {{view.cancellation?.confirmedServices?.length || 0}} / 7</p> }
          @if(league.leagueState === 'DRAFT' && view.capabilities.cancel) {
            <button type="button" (click)="confirmCancellation.set(true)" [disabled]="busy()" class="rounded-xl border border-rose-300 dark:border-rose-800 px-4 py-2 text-sm font-semibold text-rose-700 dark:text-rose-300 disabled:opacity-50 cursor-pointer">Cancelar liga</button>
            @if(confirmCancellation()) {
              <section role="alertdialog" aria-label="Confirmar cancelación" class="rounded-2xl border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 space-y-3 text-sm">
                <p>¿Cancelar esta liga para todos sus miembros? Se revocarán las invitaciones y se detendrá su preparación. No se podrá reactivar; los datos se conservarán como historial.</p>
                <button type="button" (click)="cancelLeague()" [disabled]="busy()" class="border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-2 font-semibold disabled:opacity-50 cursor-pointer">Sí, cancelar liga</button>
                <button type="button" (click)="confirmCancellation.set(false)" [disabled]="busy()" class="border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-2 font-semibold disabled:opacity-50 cursor-pointer">Volver</button>
              </section>
            }
          }

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Member Clubs Card -->
          <div class="p-6 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/90 shadow-sm space-y-4">
            <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <h3 class="text-base font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                <app-icon name="users" [size]="18" class="text-emerald-600" />
                <span>Clubes ({{ view.members.length }} / 20)</span>
              </h3>
              <span class="text-xs font-medium text-slate-500 dark:text-slate-400">
                Máximo 20 participantes
              </span>
            </div>

            <ul class="divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto pr-1">
              @for (member of view.members; track member.club_id) {
                <li class="py-2.5 flex items-center justify-between text-xs">
                  <div class="flex items-center space-x-2 min-w-0">
                    <div class="w-6 h-6 rounded-lg bg-emerald-600/10 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 flex items-center justify-center font-bold text-[11px] shrink-0">
                      {{ member.club_name.charAt(0) }}
                    </div>
                    <span class="font-semibold text-slate-800 dark:text-slate-200 truncate">
                      {{ member.club_name }} · {{ member.state }}
                    </span>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-bold"
                    [class.bg-emerald-100]="member.state === 'READY'"
                    [class.text-emerald-800]="member.state === 'READY'"
                    [class.dark:bg-emerald-950]="member.state === 'READY'"
                    [class.dark:text-emerald-300]="member.state === 'READY'"
                    [class.bg-amber-100]="member.state !== 'READY'"
                    [class.text-amber-800]="member.state !== 'READY'"
                    [class.dark:bg-amber-950]="member.state !== 'READY'"
                    [class.dark:text-amber-300]="member.state !== 'READY'"
                  >
                    {{ member.state }}
                  </span>
                </li>
              }
            </ul>

            @if (view.league.state === 'DRAFT') {
              <div class="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 text-xs text-slate-600 dark:text-slate-400 flex items-center space-x-2">
                <app-icon name="info" [size]="15" class="text-emerald-500 shrink-0" />
                <p>La activación requiere al menos dos clubes preparados.</p>
              </div>
            }

            @if (!cancelled(league.leagueState) && view.activationBlock) {
              <div class="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-xs text-amber-800 dark:text-amber-300 flex items-center space-x-2">
                <app-icon name="alert-circle" [size]="15" class="text-amber-600 dark:text-amber-400 shrink-0" />
                <p class="font-medium">{{ activationMessage(view.activationBlock) }}</p>
              </div>
            }
          </div>

          <!-- Creator Invitations & Activation Card -->
          @if (view.capabilities.manageInvitations) {
            <div class="p-6 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/90 shadow-sm space-y-5">
              <div class="pb-3 border-b border-slate-100 dark:border-slate-800">
                <div class="flex items-center justify-between">
                  <h3 class="text-base font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                    <app-icon name="key" [size]="18" class="text-emerald-600" />
                    <span>Invitaciones del creador</span>
                  </h3>
                  <span class="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    Admin
                  </span>
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Crear o revocar invitaciones no concede permisos de administración.
                </p>
              </div>

              <!-- Button to create new invitation -->
              <div class="flex items-center space-x-3">
                <button
                  type="button"
                  [disabled]="busy() || !admissionsEnabled()"
                  (click)="invite()"
                  class="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl border border-emerald-600/30 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-semibold text-xs transition-colors cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                >
                  <app-icon name="plus-circle" [size]="15" />
                  <span>Crear invitación</span>
                </button>
              </div>

              <!-- Active Created Invitation Section -->
              @for (item of invitations(); track item.id) {
                <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 space-y-3">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center space-x-1.5">
                      <app-icon name="check" [size]="14" />
                      <span>Invitación activa</span>
                    </span>
                    @if (item.token; as token) {
                    <button
                      type="button"
                      (click)="copyInviteToken(token)"
                      class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-emerald-600 flex items-center space-x-1 cursor-pointer"
                    >
                      <app-icon [name]="copiedToken() ? 'check' : 'copy'" [size]="13" />
                      <span>{{ copiedToken() ? '¡Copiado!' : 'Copiar' }}</span>
                    </button>
                    }
                  </div>

                  <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Comparte este enlace con las personas que quieras invitar. Al abrirlo podrán iniciar sesión y elegir el nombre de su club para unirse.
                  </p>

                  @if (item.token; as token) {
                  <div class="space-y-1">
                    <label [for]="'invite-' + item.id" class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">
                      Enlace de invitación
                    </label>
                    <input
                      [id]="'invite-' + item.id"
                      aria-label="Enlace de invitación"
                      readonly
                      [value]="invitationLink(token)"
                      class="w-full border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-xs font-mono bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 select-all"
                    />
                  </div>
                  } @else {
                    <p class="text-xs text-slate-500">Esta invitación sigue activa, pero su enlace no está disponible para recuperarlo. Si ya lo compartiste, seguirá funcionando hasta que caduque. Puedes revocarla y crear otra.</p>
                  }

                  <div class="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400 pt-1">
                    <p class="truncate font-mono">ID: {{ item.id }}</p>
                    <p>Caduca: {{ item.expiresAt | date:'short' }}</p>
                  </div>

                  <div class="pt-1">
                    <button
                      type="button"
                      [disabled]="busy() || !admissionsEnabled()"
                      (click)="revoke(item.id)"
                      class="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <app-icon name="trash-2" [size]="13" />
                      <span>Revocar esta invitación</span>
                    </button>
                  </div>
                </div>
              }

              <!-- Revoke by ID section -->
              <div class="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
                <label for="revoke-id-input" class="block text-xs font-medium text-slate-700 dark:text-slate-300">
                  ID de una invitación anterior que quieras revocar
                  <input
                    id="revoke-id-input"
                    [(ngModel)]="revocationId"
                    class="mt-1.5 w-full border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                    autocomplete="off"
                    placeholder="Introduce el ID del token a revocar..."
                  />
                </label>
                <button
                  type="button"
                  [disabled]="busy() || !admissionsEnabled() || !revocationId.trim()"
                  (click)="revoke(revocationId.trim())"
                  class="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50"
                >
                  <app-icon name="x-circle" [size]="13" />
                  <span>Revocar por ID</span>
                </button>
              </div>

              <!-- League Activation section -->
              <div class="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
                <button
                  type="button"
                  [disabled]="busy() || !activationEnabled() || !view.capabilities.activate"
                  (click)="activate()"
                  class="w-full inline-flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl border border-emerald-600 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-sm transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <app-icon name="zap" [size]="14" />
                  <span>Solicitar activación</span>
                </button>

                @if (operationState()) {
                  <p role="status" class="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center space-x-1.5">
                    <app-icon name="check-circle" [size]="14" />
                    <span>Activación: {{ operationState() }}</span>
                  </p>
                }
              </div>
            </div>
          }
        </div>
      }
    }
  </main>
</div>
